import { TRecursiveNonNullable } from '@api/Helper.types';
import { useGetProductOffers, useSplitTests } from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { tracking } from '@iwoca/frontend-tracking-library';
import { GetProductOffersResponse } from '@iwoca/lapi-client/edge';
import { Card, Link } from '@iwoca/orion';
import { getCurrentPathname } from '@iwRouter/LocationHelper';
import classNames from 'classnames';
import { startCase, upperFirst } from 'lodash';
import { FormattedMessage } from 'react-intl';

import {
    TProductOffersDocRequirementType,
    getOptionalDataRequirements,
    getSuggestedProduct
} from './ApplicationPendingView.helpers';
import messages from './ApplicationPendingView.messages';
import styles from './ApplicationPendingView.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import TradingHistory, {
    TGetProductOffersResponseData
} from '../../../ui/documentRequirement/TradingHistory';
import { LoadingAnimation } from '../../../ui/loadingAnimation/LoadingAnimation';
import ApplicationProgressBar from '../../../ui/progressBar/applicationProgressBar/ApplicationProgressBar';
import { PendingView } from '../PendingView';
import { PendingViewActionsDE } from './PendingViewActionsDE/PendingViewActionsDE';

const UK_SUPPORTED_DOCUMENTS: TProductOffersDocRequirementType[] = [
    'business_plan',
    'vat_return',
    'company_accounts',
    'management_accounts',
    'aged_debtor_report'
];

export type TGetProductOffersResponse =
    TRecursiveNonNullable<GetProductOffersResponse>;

type trackingApplicationStatus =
    | 'Application Submitted'
    | 'Documents Review'
    | 'Credit Analyst Review'
    | 'Your Decision';

export const mapProductStatusToStep = (
    status: TGetProductOffersResponse['data']['products'][number]['status']
) => {
    switch (status) {
        case 'accepted':
        case 'unknown':
            return 'applicationSubmitted';
        case 'pending_document_review':
        case 'pending_documents':
            return 'documentsReview';
        case 'credit_assessment_pending':
        case 'credit_assessment_required':
        case 'credit_assessment_amount_less_than_product_key':
            return 'creditAnalystReview';
        default:
            return 'yourDecision';
    }
};

export const ApplicationPendingView = () => {
    const stateKey = useStateKey();
    const businessDetailsReductionSplitTest = useSplitTests(
        'business_details_reduction'
    );
    const {
        data: productOffers,
        isLoading: isLoadingProductOffers,
        isError: isProductOffersError,
        isFetching: isFetchingProductOffers
    } = useGetProductOffers(stateKey);

    if (isLoadingProductOffers && isFetchingProductOffers)
        return (
            <MainSectionWrapper className={styles.wrapper}>
                <Card
                    className={styles.loadingContainer}
                    data-testid="application-loading"
                >
                    <LoadingAnimation />
                    <h2>
                        <FormattedMessage {...messages.loadingHeader} />
                    </h2>
                    <p>
                        <FormattedMessage {...messages.loadingDescription} />
                    </p>
                </Card>
            </MainSectionWrapper>
        );

    if (isProductOffersError) return <FallbackErrorModal />;

    const suggestedProduct = getSuggestedProduct(productOffers);

    // A suggested product should always be present in the backend for product_offer endpoint but handling the cases here when it's not loaded in properly.
    if (!suggestedProduct) return <PendingView />;

    const optionalDocs: ReturnType<typeof getOptionalDataRequirements> =
        getOptionalDataRequirements(suggestedProduct, UK_SUPPORTED_DOCUMENTS);

    const productOfferStatus = () => {
        return mapProductStatusToStep(
            suggestedProduct.status as TGetProductOffersResponse['data']['products'][number]['status']
        );
    };

    if (productOfferStatus && optionalDocs) {
        const optionalDocsList = optionalDocs.map((doc) => doc.type);

        tracking.ampli.applicationPendingPageViewed({
            'Aged debtor report required': optionalDocsList.includes(
                'aged_debtor_report'
            )
                ? 'Optional'
                : 'Not required',
            'Company accounts required': optionalDocsList.includes(
                'company_accounts'
            )
                ? 'Optional'
                : 'Not required',
            'Management accounts required': optionalDocsList.includes(
                'management_accounts'
            )
                ? 'Optional'
                : 'Not required',
            application_pending_status: startCase(
                upperFirst(productOfferStatus())
            ) as trackingApplicationStatus
        });
    }

    return (
        <MainSectionWrapper className={styles.wrapper}>
            <Card className={classNames(styles.card, styles.progress)}>
                <h2 className={styles.progressHeader}>
                    <FormattedMessage {...messages.title} />
                </h2>
                {productOfferStatus && (
                    <ApplicationProgressBar appStep={productOfferStatus()} />
                )}
                <p
                    className={styles.progressDescription}
                    data-testid="application-pending-view-description"
                >
                    <FormattedMessage
                        {...messages.description}
                        values={{
                            phoneNumber: (
                                <Link
                                    href={`tel:${
                                        getCountry() === 'DE'
                                            ? '06994323020'
                                            : messages.phoneNumber
                                                  .defaultMessage
                                    }`}
                                    onClick={() =>
                                        tracking.ampli.phoneNumberClicked({
                                            location_clicked_from: `Application progress bar: ${startCase(
                                                upperFirst(productOfferStatus())
                                            )}`,
                                            url: getCurrentPathname()
                                        })
                                    }
                                >
                                    <FormattedMessage
                                        {...messages.phoneNumber}
                                    />
                                </Link>
                            ),
                            emailAddress: getCountry() === 'DE' && (
                                <Link href="mailto:support@iwoca.de">
                                    support@iwoca.de
                                </Link>
                            )
                        }}
                    />
                </p>
            </Card>
            {((getCountry() === 'UK' &&
                productOfferStatus() === 'documentsReview' &&
                optionalDocs.length > 0) ||
                businessDetailsReductionSplitTest) && (
                <Card className="mt-[15px] p-[25px]">
                    <TradingHistory
                        optionalDocs={
                            optionalDocs as TGetProductOffersResponseData['products'][number]['data_requirements']
                        }
                    />
                </Card>
            )}
            <DEOnly>
                <PendingViewActionsDE
                    suggestedProductStatus={suggestedProduct.status}
                    productOffersStatus={productOfferStatus()}
                />
            </DEOnly>
        </MainSectionWrapper>
    );
};
